import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
  if (!cardCode) return null;

  try {
    if (cardCode.length < 2) {
      return null;
    }

    const cleanCode = cardCode.replace(/\.jpg$/i, '');
    return `/assets/img/cards/${cleanCode}.png`;
  } catch (e) {
    return null;
  }
};

// Helper function to parse card string for Queen
// Format: "5CC,QHH,1,1,1,1,1,1,1,1,1,1,1,1,1,1"
// Cards are distributed: 2 cards per total (Total 0, 1, 2, 3)
// "1" means no card for that position
const parseQueenCards = (cardString) => {
  if (!cardString) return { totals: [] };

  const totals = [];
  const allItems = cardString.split(',').map(c => c.trim());
  const cardPattern = /^[2-9AJQK]?\d*[SDCH]{2}$/i;

  // Distribute cards: 2 cards per total (Total 0, 1, 2, 3)
  for (let i = 0; i < 4; i++) {
    const startIdx = i * 2;
    const cards = [];

    for (let j = 0; j < 2; j++) {
      const item = allItems[startIdx + j];
      if (item && item !== '1' && cardPattern.test(item)) {
        cards.push(item);
      }
    }

    totals[i] = {
      total: i,
      cards: cards,
      totalValue: 0
    };
  }

  return { totals };
};

// Calculate total value from card codes
const calculateTotal = (cards) => {
  if (!cards || cards.length === 0) return 0;

  let total = 0;
  cards.forEach(cardCode => {
    const rankMatch = cardCode.match(/^(\d+|[AJQK])/i);
    if (rankMatch) {
      const rank = rankMatch[1].toUpperCase();
      if (rank === 'A') {
        total += 1;
      } else if (rank === 'J') {
        total += 11;
      } else if (rank === 'Q') {
        total += 12; // Queen is 12
      } else if (rank === 'K') {
        total += 13;
      } else {
        const num = parseInt(rank, 10);
        total += num || 0;
      }
    }
  });

  return total;
};

const ResultQueen = ({
  data = [],
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('queen');
        if (response?.success && response?.data?.data?.res) {
          setLastResults(response.data.data.res);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);

    const mid = resultItem?.mid;

    // Only fetch if we don't already have this detail result
    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults('queen', mid.toString());
        if (response?.success && response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  }, [detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data based on selected result - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    // Use detailResult if available
    const cardData = detailResult?.card ? parseQueenCards(detailResult.card) : { totals: [] };
    const winValue = detailResult?.win || selectedResult.win;
    const winnerTotal = parseInt(winValue, 10);
    const winner = detailResult?.winnat || `Total ${winnerTotal}`;

    // Build totals array with cards and calculated totals
    const totals = [];
    for (let i = 0; i < 4; i++) {
      const totalData = cardData.totals[i];
      if (totalData && totalData.cards.length > 0) {
        const totalValue = calculateTotal(totalData.cards);
        totals[i] = {
          total: i,
          cards: totalData.cards.map(card => ({
            code: card,
            image: getCardImage(card)
          })),
          totalValue: totalValue,
          isWinner: i === winnerTotal
        };
      } else {
        totals[i] = {
          total: i,
          cards: [],
          totalValue: 0,
          isWinner: i === winnerTotal
        };
      }
    }

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      totals: totals,
      winner: winner,
      winnerTotal: winnerTotal,
      resultDetails: {
        winner: winner,
        rdesc: detailResult?.rdesc || ""
      }
    };
  }, [selectedResult, detailResult, gameData, showModal]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : data.map((res, idx) => ({ win: res, mid: null }));

  return (
    <>
      <div className="result-row">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          const winValue = win?.toString() || "0";

          return (
            <div
              key={index}
              className="result-circle-queen"
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
            >
              {winValue}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Queen Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div>
            {/* Round ID */}
            <h6 className="text-right round-id" style={{ marginBottom: '1rem' }}>
              <b>Round Id:</b> {modalData.roundId}
            </h6>

            {/* Four Totals Display Section */}
            <div className="col-md-12">
              <div className="player-image-container row">
                {modalData.totals.map((totalData, index) => (
                  <div key={index} className="col-md-12 text-center">
                    <h4>
                      Total {totalData.total} - <span>{totalData.totalValue}</span>
                    </h4>
                    <div className="d-inline-block">
                      {totalData.cards.map((card, cardIndex) => (
                        card.image && (
                          <img
                            key={cardIndex}
                            src={card.image}
                            alt={`Card ${card.code}`}
                            className="d-inline-block mr-1"
                            onError={(e) => {
                              const cardName = card.image.split('/').pop();
                              const fallbackPath = `/assets/img/cards/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div>
                      {totalData.isWinner ? (
                        <label className="winner-label bg-success m-t-5">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-5">&nbsp;</label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Result Details */}
            <div className="col-md-12 text-left">
              <p className="m-b-0 m-t-20">
                <b>Winner:</b> {modalData.resultDetails.winner || `Total ${modalData.winnerTotal}`}
              </p>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultQueen;

